/**
 * ChannelsLineup template component.
 */
import Animator from '@components/elements/animator/animator';
import Channel from '@shared/classes/channel';
import ChannelListBackground from '@components/sections/channelList/channelListBackground';
import ChannelListContent from '@components/sections/channelList/channelListContent';
import ChannelListContext from '@shared/components/utils/context/channelListContext';
import ChannelUtils from '@shared/utils/channels';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import MathUtils from '@shared/utils/math';
import Modals from '@shared/types/enums/modals';
import Pages from '@shared/types/enums/pages';
import Plan from '@shared/classes/plan';
import Routes from '@shared/types/enums/routes';
import goToChannel from '@shared/actions/channels/goToChannel';
import useEpg from '@shared/components/utils/hooks/useEpg';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { ChannelListProps as Props } from '@shared/types/props/channelList';
import { Coordinates } from '@shared/utils/floatingMenu';
import { eventsSelector } from '@shared/store/slices/events.slice';
import { loginAction } from '@shared/actions/flow';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { selectMenuCollapse } from '@shared/store/slices/menu.slice';
import { setModal, setModalParams } from '@shared/store/slices/modal.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';
import {
  closeFloatingMenu,
  openFloatingMenu,
} from '@shared/store/thunks/floatingMenu.thunk';
import {
  selectCurrentParams,
  selectRoute,
  updateParams,
} from '@shared/store/slices/router.slice';

/**
 * Misc constants
 */
const CHUNK_SIZE = 5;
const FLOATING_DELAY = 1.5 * 1000;
const PAGINATION_OFFSET = {
  horizontal: 3,
};

export default function ChannelsLineupTemplate({
  channelsData,
  currentChannel,
  currentEvents,
  currentIndex,
}: Props) {
  const dispatch = useAppDispatch();
  const { eventsData } = useAppSelector(eventsSelector);
  const currentParams = useAppSelector(selectCurrentParams);
  const focus = useAppSelector(selectFocus);
  const menuExpanded = !useAppSelector(selectMenuCollapse);
  const route = useAppSelector(selectRoute);
  const user = useAppSelector(userSelector);

  const [channel, setChannel] = useState(currentChannel);
  const [event, setEvent] = useState(
    channel && currentEvents && currentEvents[channel.id]
  );
  const [futureEvents, setFutureEvents] = useState(false);
  const [pastEvents, setPastEvents] = useState(false);

  // Epg hook

  const { complete, epg, onPagination } = useEpg({
    channelId: channel?.id,
    chunkSize: CHUNK_SIZE,
    offset: PAGINATION_OFFSET,
    ready:
      focus === Focus.CHANNEL_LIST_EVENTS ||
      (channel?.id !== undefined && (eventsData[channel.id]?.length || 0) < 2),
  });

  // References

  const channelSelectedRef = useRef<Channel | undefined>(channel);
  const eventIndexRef = useRef(0);
  const delayTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const epgRef = useRef(epg);
  const planRef = useRef<Plan | null>(null);
  const pageRef = useRef(route.Page);

  channelSelectedRef.current = channel;
  epgRef.current = epg;
  pageRef.current = route.Page;
  planRef.current = ChannelUtils.getUpgradablePlan(channel, false);

  // Memoized values

  const context = useMemo(() => {
    const focused =
      focus === -1 ||
      focus === Focus.CHANNEL_LIST ||
      focus === Focus.FLOATING_MENU ||
      focus === Focus.CHANNEL_LIST_EVENTS ||
      focus === Focus.CHANNEL_LIST_UPGRADE ||
      focus === Focus.MODAL_BUTTONS;

    return {
      channel,
      complete,
      event,
      focused,
      index: currentIndex,
      plan: planRef.current,
      pastEvents,
      futureEvents,
    };
  }, [channel, event, complete, focus, futureEvents, pastEvents]);

  // UI handlers

  const toggleFloatingMenu = useCallback((open = true) => {
    const asset = channelSelectedRef?.current
      ? epgRef.current[channelSelectedRef?.current?.id][
          eventIndexRef.current
        ] || null
      : null;
    if (open && asset) {
      dispatch(
        updateParams({
          channel: channelSelectedRef.current,
          stacked: true,
        })
      );
      const route = {
        asset,
        position: Coordinates.CHANNELS_LINEUP,
        route: Routes.CHANNELS_LINEUP,
      };
      dispatch(openFloatingMenu(route));
    } else {
      dispatch(closeFloatingMenu());
    }
  }, []);

  // Event handlers

  const onPointerDown = useCallback(() => {
    clearTimeout(delayTimeoutRef.current);
    if (focus !== Focus.FLOATING_MENU) {
      delayTimeoutRef.current = setTimeout(() => {
        toggleFloatingMenu();
      }, FLOATING_DELAY);
    } else {
      toggleFloatingMenu(false);
    }
  }, [focus]);

  const onPointerUp = useCallback(() => {
    clearTimeout(delayTimeoutRef.current);
  }, []);

  // Effect hooks

  useEffect(() => {
    (currentParams?.stacked ||
      focus === Focus.CHANNELS_CHOICE ||
      focus === Focus.RAILS_LIST) &&
      dispatch(setFocus(Focus.CHANNEL_LIST));
    return () => {
      dispatch(closeFloatingMenu());
    };
  }, []);

  useEffect(() => {
    // Set new current event and initial index
    if (event && (event?.channel?.id !== channel?.id || event.mocked)) {
      eventIndexRef.current = Math.max(
        channel?.getEventsRange(epgRef.current).currentIndex || 0,
        0
      );
      const newEvent = channel
        ? (currentEvents &&
            !currentEvents[channel.id]?.mocked &&
            currentEvents[channel.id]) ||
          (epgRef.current[channel.id]?.length
            ? epgRef.current[channel.id][eventIndexRef.current]
            : undefined)
        : undefined;
      setEvent(newEvent || channel?.getCurrentEvent() || undefined);
    }

    // Check if there are future/past events
    if (channel) {
      const { currentIndex, lastIndex } =
        channel.getEventsRange(epgRef.current) || {};
      setPastEvents(eventIndexRef.current > currentIndex);
      setFutureEvents(eventIndexRef.current < lastIndex);
    } else {
      setPastEvents(false);
      setFutureEvents(false);
    }
  }, [channel, complete, epg]);

  // Keyboard hooks

  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key) => {
      if (key === 'RIGHT') {
        dispatch(setFocus(Focus.CHANNEL_LIST));
      }
    },
  });

  useKeyboard({
    focus: Focus.CHANNEL_LIST,
    keyDownHandler: (key) => {
      // If no channel selected, exit
      if (!channelSelectedRef.current) return;
      // Fetch current channel events range
      switch (key) {
        case 'BACK':
          if (route.Page === Pages.CHANNELS_LINEUP) {
            dispatch(popRoute());
          } else {
            dispatch(setFocus(Focus.MENU));
          }
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
        case 'RIGHT':
          if (
            !epgRef.current[channelSelectedRef.current.id] ||
            epgRef.current[channelSelectedRef.current.id].length < 2
          ) {
            if (planRef.current) dispatch(setFocus(Focus.CHANNEL_LIST_UPGRADE));
            return;
          }
          setTimeout(() => {
            dispatch(setFocus(Focus.CHANNEL_LIST_EVENTS));
          });
      }
    },
    keyPressHandler: () => {
      const { floatingMenu } = store.getState();
      if (!floatingMenu.open) {
        toggleFloatingMenu();
      }
    },
  });

  useKeyboard({
    focus: Focus.CHANNEL_LIST_EVENTS,
    keyDownHandler: (key) => {
      // If no channel selected, exit
      if (!channelSelectedRef.current) return;

      // Fetch current channel events range
      const { currentIndex, lastIndex } =
        channelSelectedRef.current.getEventsRange(epgRef.current) || {};
      let newIndex = eventIndexRef.current;
      switch (key) {
        case 'BACK':
          dispatch(setFocus(Focus.CHANNEL_LIST));
          return;
        //@ts-ignore
        case 'LEFT':
          // Focus menu if left key is pressed and index has not changed
          if (newIndex === currentIndex) {
            dispatch(setFocus(Focus.CHANNEL_LIST));
            return;
          }
        case 'RIGHT':
          // Calculate new event index based on key and range
          newIndex = key === 'LEFT' ? newIndex - 1 : newIndex + 1;
          eventIndexRef.current = MathUtils.numberWithinRange(
            newIndex,
            currentIndex,
            lastIndex
          );

          // Fetch and set new event
          const event = channelSelectedRef.current?.getEventByIndex(
            eventIndexRef.current,
            epgRef.current
          );
          setPastEvents(eventIndexRef.current > currentIndex);
          setFutureEvents(eventIndexRef.current < lastIndex);
          setEvent((currentEvent) => {
            return event || currentEvent;
          });

          // Paginate when minimum offset is reached
          if (
            eventIndexRef.current <= lastIndex &&
            lastIndex - eventIndexRef.current < PAGINATION_OFFSET.horizontal
          )
            onPagination(null, Direction.HORIZONTAL);
          break;
        case 'DOWN':
          if (planRef.current) dispatch(setFocus(Focus.CHANNEL_LIST_UPGRADE));
          break;
      }
    },
    keyUpHandler: (key) => {
      if (key === 'SELECT') {
        goToChannel({
          channel: channelSelectedRef.current,
          checkParental: true,
          pop: !!route.stack.length,
        });
      }
    },
    keyPressHandler: () => {
      const { floatingMenu } = store.getState();
      if (!floatingMenu.open) {
        toggleFloatingMenu();
      }
    },
  });

  useKeyboard({
    focus: Focus.CHANNEL_LIST_UPGRADE,
    keyDownHandler: (key) => {
      switch (key) {
        case 'BACK':
          dispatch(setFocus(Focus.CHANNEL_LIST));
          return;
        //@ts-ignore
        case 'LEFT':
          dispatch(setFocus(Focus.CHANNEL_LIST));
          break;
        case 'UP':
          dispatch(setFocus(Focus.CHANNEL_LIST_EVENTS));
          break;
        case 'SELECT':
          if (user.isAnonymous) {
            loginAction();
          } else {
            const plan = ChannelUtils.getUpgradablePlan(
              channelSelectedRef.current,
              false
            );
            dispatch(
              setModalParams({
                channel: channelSelectedRef.current,
                plan,
              })
            );
            dispatch(setModal(Modals.UPGRADE_PLAN));
          }
          break;
      }
    },
  });

  return (
    <ChannelListContext.Provider value={context}>
      <ChannelListBackground />
      <Animator
        id={'channelLineupContainer'}
        collapse={{ horizontal: menuExpanded }}
        type={Animator.Type.CHANNELS_LINEUP}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        <ChannelListContent data={channelsData} setChannel={setChannel} />
      </Animator>
    </ChannelListContext.Provider>
  );
}
